import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { API } from "@/lib/api";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  BarChart3, Megaphone, FileText, Radar, RefreshCw, Users,
} from "lucide-react";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 0, label: "All time" },
];

const KIND_LABEL = {
  article: "Article",
  "blog-hub": "Blog hub",
  service: "Service page",
  homepage: "Homepage",
  contact: "Contact",
  other: "Other",
};

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="gw-card gw-stat" data-testid={testId}>
      <div className="gw-stat-icon"><Icon size={18} aria-hidden="true" /></div>
      <div>
        <div className="gw-stat-value">{value}</div>
        <div className="gw-stat-label">{label}</div>
        {sub && <div className="gw-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

function BarList({ items, total, testId }) {
  if (!items?.length) {
    return <p className="gw-muted" data-testid={`${testId}-empty`}>No data in this period.</p>;
  }
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <ul className="gw-barlist" data-testid={testId}>
      {items.map((i) => (
        <li key={i.label}>
          <div className="gw-barlist-row">
            <span className="gw-barlist-label" title={i.label}>{i.label}</span>
            <span className="gw-barlist-count">
              {i.count}{total ? ` · ${Math.round((i.count / total) * 100)}%` : ""}
            </span>
          </div>
          <div className="gw-barlist-track">
            <div className="gw-barlist-fill" style={{ width: `${(i.count / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminContacts() {
  const [creds, setCreds] = useState({ user: "", pass: "" });
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [indexnow, setIndexnow] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);

  const cfg = useCallback(
    () => ({ auth: { username: creds.user, password: creds.pass } }),
    [creds]
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, sum, ana, idx] = await Promise.all([
        axios.get(`${API}/admin/contacts`, cfg()),
        axios.get(`${API}/admin/contacts/summary`, cfg()),
        axios.get(`${API}/admin/leads/analytics?days=${days}`, cfg()),
        axios.get(`${API}/admin/indexnow/status`, cfg()),
      ]);
      setRows(list.data);
      setSummary(sum.data);
      setAnalytics(ana.data);
      setIndexnow(idx.data);
      setAuthed(true);
    } catch (err) {
      setError(err.response?.status === 401 ? "Invalid credentials" : "Unable to load submissions");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  // Refetch analytics when the range changes (post-login only).
  useEffect(() => {
    if (!authed) return;
    let live = true;
    axios
      .get(`${API}/admin/leads/analytics?days=${days}`, cfg())
      .then((res) => { if (live) setAnalytics(res.data); })
      .catch(() => {});
    return () => { live = false; };
  }, [days, authed, cfg]);

  const triggerPing = async () => {
    setPinging(true);
    setPingResult(null);
    try {
      const res = await axios.post(`${API}/admin/indexnow/ping`, null, cfg());
      setPingResult(res.data);
      const idx = await axios.get(`${API}/admin/indexnow/status`, cfg());
      setIndexnow(idx.data);
    } catch (err) {
      setPingResult({ status: "error", detail: "Request failed — try again." });
    } finally {
      setPinging(false);
    }
  };

  const topCampaign = analytics?.by_campaign?.find((c) => c.label !== "(no campaign)");
  const topPage = analytics?.by_landing_page?.[0];
  const lastPing = indexnow?.recent_pings?.[0];

  return (
    <div className="gw-container gw-section">
      <Helmet>
        <title>Lead Dashboard Admin | Garcia-Windsor, P.C.</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <h1>Lead Dashboard</h1>
      <p className="gw-muted">Private admin view. Not indexed.</p>

      {!authed ? (
        <div className="gw-card" style={{ maxWidth: 420 }}>
          <form onSubmit={(e) => { e.preventDefault(); load(); }}>
            <label className="gw-label">Username
              <input className="gw-input" data-testid="admin-username-input" value={creds.user} onChange={(e) => setCreds({ ...creds, user: e.target.value })} />
            </label>
            <label className="gw-label" style={{ marginTop: 12 }}>Password
              <input className="gw-input" data-testid="admin-password-input" type="password" value={creds.pass} onChange={(e) => setCreds({ ...creds, pass: e.target.value })} />
            </label>
            <button className="gw-btn gw-btn-primary" data-testid="admin-signin-btn" style={{ marginTop: 16, width: "100%" }} type="submit" disabled={loading}>
              {loading ? "Loading…" : "Sign in"}
            </button>
            {error && <p data-testid="admin-auth-error" style={{ color: "var(--gw-error)", marginTop: 12 }}>{error}</p>}
          </form>
        </div>
      ) : (
        <Tabs defaultValue="dashboard" className="gw-admin-tabs">
          <TabsList className="gw-tabslist" data-testid="admin-tabs">
            <TabsTrigger value="dashboard" className="gw-tab" data-testid="tab-dashboard">
              <BarChart3 size={15} aria-hidden="true" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="submissions" className="gw-tab" data-testid="tab-submissions">
              <Users size={15} aria-hidden="true" /> Submissions
            </TabsTrigger>
          </TabsList>

          {/* ================= DASHBOARD ================= */}
          <TabsContent value="dashboard">
            <div className="gw-range" role="group" aria-label="Date range">
              {RANGES.map((r) => (
                <button
                  key={r.days}
                  type="button"
                  data-testid={`range-${r.days}`}
                  className={`gw-range-btn${days === r.days ? " active" : ""}`}
                  onClick={() => setDays(r.days)}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {analytics && (
              <>
                <div className="gw-grid gw-grid-4 gw-statgrid">
                  <StatCard icon={Users} label="Leads in period" value={analytics.total} testId="stat-total-leads" />
                  <StatCard
                    icon={Megaphone}
                    label="With campaign data"
                    value={analytics.with_attribution}
                    sub={analytics.total ? `${Math.round((analytics.with_attribution / analytics.total) * 100)}% of leads` : null}
                    testId="stat-with-attribution"
                  />
                  <StatCard icon={BarChart3} label="Top campaign" value={topCampaign ? topCampaign.label : "—"} sub={topCampaign ? `${topCampaign.count} lead(s)` : "No UTM campaigns yet"} testId="stat-top-campaign" />
                  <StatCard icon={FileText} label="Top landing page" value={topPage ? topPage.path : "—"} sub={topPage ? `${KIND_LABEL[topPage.kind]} · ${topPage.count} lead(s)` : null} testId="stat-top-page" />
                </div>

                <div className="gw-card" data-testid="leads-timeline-card">
                  <h2 className="gw-card-title">Leads per day</h2>
                  {analytics.timeline.length === 0 ? (
                    <p className="gw-muted">No leads in this period yet.</p>
                  ) : (
                    <div style={{ width: "100%", height: 220 }}>
                      <ResponsiveContainer>
                        <BarChart data={analytics.timeline} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--gw-border)" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--gw-muted)" }} tickFormatter={(d) => d.slice(5)} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--gw-muted)" }} />
                          <Tooltip
                            cursor={{ fill: "rgba(122,27,42,0.06)" }}
                            contentStyle={{ borderRadius: 8, border: "1px solid var(--gw-border)", fontSize: 13 }}
                          />
                          <Bar dataKey="count" name="Leads" fill="var(--gw-primary)" radius={[4, 4, 0, 0]} maxBarSize={34} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div className="gw-grid gw-grid-2 gw-dashrow">
                  <div className="gw-card" data-testid="campaigns-card">
                    <h2 className="gw-card-title">Leads by campaign</h2>
                    <BarList items={analytics.by_campaign} total={analytics.total} testId="campaigns-barlist" />
                  </div>
                  <div className="gw-card" data-testid="sources-card">
                    <h2 className="gw-card-title">Leads by source / medium</h2>
                    <BarList items={analytics.by_source_medium} total={analytics.total} testId="sources-barlist" />
                  </div>
                </div>

                <div className="gw-card" data-testid="landing-pages-card">
                  <h2 className="gw-card-title">Which pages convert — landing pages that produced leads</h2>
                  {analytics.by_landing_page.length === 0 ? (
                    <p className="gw-muted">No landing-page data in this period.</p>
                  ) : (
                    <table className="gw-admin-table" data-testid="landing-pages-table">
                      <thead>
                        <tr><th>Page</th><th>Type</th><th style={{ textAlign: "right" }}>Leads</th></tr>
                      </thead>
                      <tbody>
                        {analytics.by_landing_page.map((p) => (
                          <tr key={p.path}>
                            <td><a href={p.path} target="_blank" rel="noreferrer">{p.path}</a></td>
                            <td><span className={`gw-kind gw-kind-${p.kind}`}>{KIND_LABEL[p.kind]}</span></td>
                            <td style={{ textAlign: "right", fontWeight: 700 }}>{p.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* ============ SEARCH ENGINE NOTIFICATIONS ============ */}
            {indexnow && (
              <div className="gw-card" data-testid="indexnow-card">
                <h2 className="gw-card-title"><Radar size={16} aria-hidden="true" style={{ verticalAlign: "-2px", marginRight: 6 }} />Search engine notifications (IndexNow)</h2>
                <div className="gw-grid gw-grid-3 gw-idxgrid">
                  <div><strong data-testid="indexnow-sitemap-urls">{indexnow.sitemap_urls}</strong><span>URLs in sitemaps</span></div>
                  <div><strong data-testid="indexnow-pending">{indexnow.pending_changes}</strong><span>changes queued</span></div>
                  <div>
                    <strong data-testid="indexnow-last-ping">{lastPing ? (lastPing.status === "ok" ? "Sent" : lastPing.status) : "never"}</strong>
                    <span>last notification{lastPing ? ` · ${new Date(lastPing.at).toLocaleDateString()}` : ""}</span>
                  </div>
                </div>
                <p className="gw-muted gw-idxnote">
                  New or updated pages are pushed automatically to Bing, Yandex, Seznam and Naver via the
                  IndexNow protocol whenever the sitemap changes. Google retired its ping endpoint — it
                  discovers changes through this site's robots.txt sitemap listings and per-URL lastmod
                  dates, which update automatically.
                </p>
                <button
                  type="button"
                  className="gw-btn gw-btn-primary"
                  data-testid="indexnow-ping-btn"
                  onClick={triggerPing}
                  disabled={pinging || indexnow.pending_changes === 0}
                >
                  <RefreshCw size={14} aria-hidden="true" style={{ marginRight: 6, verticalAlign: "-2px" }} />
                  {pinging ? "Notifying…" : indexnow.pending_changes === 0 ? "Everything already submitted" : `Notify search engines now (${indexnow.pending_changes})`}
                </button>
                {pingResult && (
                  <p
                    data-testid="indexnow-ping-result"
                    className="gw-idxresult"
                    style={{ color: pingResult.status === "ok" ? "var(--gw-success)" : pingResult.status === "skipped" ? "var(--gw-muted)" : "var(--gw-error)" }}
                  >
                    {pingResult.status === "ok"
                      ? `Submitted ${pingResult.submitted} URL(s) — ${pingResult.detail}`
                      : pingResult.detail}
                  </p>
                )}
              </div>
            )}
          </TabsContent>

          {/* ================= SUBMISSIONS ================= */}
          <TabsContent value="submissions">
            {summary && (
              <div className="gw-grid gw-grid-3" style={{ marginBottom: 24 }}>
                <div className="gw-card"><strong>Total submissions:</strong> {summary.total}</div>
                <div className="gw-card"><strong>English:</strong> {summary.by_language.en}</div>
                <div className="gw-card"><strong>Español:</strong> {summary.by_language.es}</div>
              </div>
            )}
            <div className="gw-card" style={{ padding: 0, overflow: "auto" }}>
              <table className="gw-admin-table" data-testid="submissions-table">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Lang</th>
                    <th>Case</th>
                    <th>Campaign</th>
                    <th>Landing page</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: "center", padding: 24 }}>No submissions yet.</td></tr>
                  )}
                  {rows.map((r) => {
                    const attr = r.attribution || {};
                    const landing = (attr.landing_page || r.source_page || "").replace(/^https?:\/\/[^/]+/, "").split("?")[0];
                    return (
                      <tr key={r.id}>
                        <td>{new Date(r.submitted_at).toLocaleString()}</td>
                        <td>{r.name}</td>
                        <td><a href={`tel:${r.phone}`}>{r.phone}</a></td>
                        <td><a href={`mailto:${r.email}`}>{r.email}</a></td>
                        <td>{r.preferred_language}</td>
                        <td>{r.case_type}</td>
                        <td>{attr.utm_campaign || "—"}</td>
                        <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={landing}>{landing || "—"}</td>
                        <td style={{ maxWidth: 320 }}>{r.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      )}

      <style>{`
        .gw-admin-table { width: 100%; border-collapse: collapse; }
        .gw-admin-table th, .gw-admin-table td { padding: 12px 14px; text-align: left; border-bottom: 1px solid var(--gw-border); font-size: 0.92rem; }
        .gw-admin-table th { background: var(--gw-surface); font-weight: 700; color: var(--gw-primary-dark); }
        .gw-admin-tabs { margin-top: 8px; }
        .gw-tabslist { display: inline-flex; gap: 4px; background: var(--gw-surface); border: 1px solid var(--gw-border); border-radius: 10px; padding: 4px; margin-bottom: 20px; height: auto; }
        .gw-tab { display: inline-flex; align-items: center; gap: 7px; padding: 9px 18px; border-radius: 7px; font-weight: 600; font-size: 0.95rem; color: var(--gw-muted); background: transparent; }
        .gw-tab[data-state="active"] { background: var(--gw-primary); color: #FFF9E8; box-shadow: none; }
        .gw-tab:focus-visible { outline: 3px solid var(--gw-accent); }
        .gw-range { display: flex; gap: 8px; margin-bottom: 18px; flex-wrap: wrap; }
        .gw-range-btn { padding: 7px 14px; border: 1px solid var(--gw-border-strong); border-radius: 999px; background: #fff; font-size: 0.88rem; font-weight: 600; color: var(--gw-muted); cursor: pointer; transition: background 0.15s ease, color 0.15s ease; }
        .gw-range-btn:hover { color: var(--gw-primary); }
        .gw-range-btn.active { background: var(--gw-primary); border-color: var(--gw-primary); color: #FFF9E8; }
        .gw-range-btn:focus-visible { outline: 3px solid var(--gw-accent); }
        .gw-statgrid { margin-bottom: 20px; gap: 14px; }
        .gw-grid-4 { grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); }
        @media (min-width: 1024px) { .gw-grid-4 { grid-template-columns: repeat(4, 1fr); } }
        .gw-stat { display: flex; gap: 14px; align-items: flex-start; }
        .gw-stat-icon { width: 38px; height: 38px; border-radius: 9px; background: var(--gw-surface); border: 1px solid var(--gw-border); display: grid; place-items: center; color: var(--gw-primary); flex-shrink: 0; }
        .gw-stat-value { font-family: var(--gw-font-serif); font-size: 1.3rem; font-weight: 700; color: var(--gw-primary-dark); line-height: 1.2; word-break: break-word; }
        .gw-stat-label { font-size: 0.82rem; color: var(--gw-muted); margin-top: 2px; }
        .gw-stat-sub { font-size: 0.78rem; color: var(--gw-accent-hover); margin-top: 2px; font-weight: 600; }
        .gw-card-title { font-size: 1.05rem; margin: 0 0 14px; color: var(--gw-primary-dark); }
        .gw-dashrow { gap: 14px; margin: 20px 0; }
        .gw-dashrow .gw-card { margin: 0; }
        .gw-barlist { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
        .gw-barlist-row { display: flex; justify-content: space-between; gap: 12px; font-size: 0.9rem; margin-bottom: 4px; }
        .gw-barlist-label { font-weight: 600; color: var(--gw-primary-dark); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .gw-barlist-count { color: var(--gw-muted); flex-shrink: 0; }
        .gw-barlist-track { height: 8px; border-radius: 999px; background: var(--gw-surface); border: 1px solid var(--gw-border); overflow: hidden; }
        .gw-barlist-fill { height: 100%; border-radius: 999px; background: var(--gw-primary); transition: width 0.25s ease; }
        .gw-kind { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 0.75rem; font-weight: 700; background: var(--gw-surface); border: 1px solid var(--gw-border-strong); color: var(--gw-muted); }
        .gw-kind-article { background: rgba(212,160,23,0.14); border-color: var(--gw-accent); color: var(--gw-accent-hover); }
        .gw-kind-service { background: rgba(122,27,42,0.08); border-color: var(--gw-primary); color: var(--gw-primary); }
        .gw-idxgrid { gap: 10px; margin-bottom: 12px; }
        .gw-idxgrid > div { background: var(--gw-surface); border: 1px solid var(--gw-border); border-radius: 10px; padding: 12px 14px; display: flex; flex-direction: column; }
        .gw-idxgrid strong { font-size: 1.15rem; color: var(--gw-primary-dark); }
        .gw-idxgrid span { font-size: 0.78rem; color: var(--gw-muted); }
        .gw-idxnote { font-size: 0.85rem; margin: 0 0 14px; }
        .gw-idxresult { margin-top: 12px; font-size: 0.88rem; font-weight: 600; }
        @media (max-width: 768px) {
          .gw-statgrid, .gw-dashrow, .gw-idxgrid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
